<template>
  <div
    id="#auth-form"
    class="form-auth"
  >
    <ObservedForm
      ref="observer"
      data-testid="form-auth"
      :error-messages="[ errorMessage ]"
      @submit="submit"
    >
      <VContainer class="px-0 v2-padding">
        <VRow v-if="demo && !errorMessage && allowPersist" class="v2-gutters">
          <VCol>
            <ErrorContainer
              :messages="[$t('demo.error.newSignIn')]"
              class="ma-0 px-6"
              large
              data-testid="demo-new-sign-in"
            />
          </VCol>
        </VRow>
        <VRow v-if="slots.header" class="v2-gutters">
          <VCol class="text-center">
            <slot name="header" />
          </VCol>
        </VRow>
        <VRow class="v2-gutters">
          <VCol>
            <p v-if="alreadyExists">
              <SBtn
                class="ma-3 mt-0 font-weight-bold text--wrap"
                link-medium
                data-testid="email-exists-reset-btn"
                @mousedown.prevent
                @click="resetAlreadyExists"
              >
                {{ $t('error.p.emailExists.link') }}
              </SBtn>
            </p>
            <TextFieldWithValidation
              ref="userName"
              v-model="username"
              :label="signInLabel"
              rules="required"
              :readonly="!!usernameReadonly"
              name="email"
              data-testid="auth-email"
              hide-details-without-error
              autocomplete="username"
              required
            />
            <PasswordField
              v-model="password"
              name="regPassword"
              :alternate-rules="SIGN_IN_PASSWORD_RULES"
              data-testid="auth-password"
              hide-details-without-error
              autocomplete="current-password"
            />
          </VCol>
        </VRow>
        <VRow v-if="options" density="compact" class="mt-n4 mb-2 v2-gutters-dense">
          <VCol v-if="allowPersist" class="px-0">
            <VCheckbox
              v-model="persist"
              :label="$t('auth.label.rememberMe')"
              name="persist"
              hide-details
              class="text-left mt-0"
            />
          </VCol>
          <VCol v-if="!agent" class="text-right">
            <SBtn
              id="reset-link"
              class="reset-link"
              height="14px"
              link-medium
              @click="emits('reset-password')"
            >
              {{ $t('account.link.forgotPassword') }}
            </SBtn>
          </VCol>
        </VRow>
        <VRow no-gutters class="mt-0">
          <VCol>
            <SBtn
              id="login-btn"
              :color="btnColor"
              action
              type="submit"
              data-testid="auth-submit"
              block
            >
              {{ signInBtnText ? signInBtnText : $t('account.signin') }}
            </SBtn>
          </VCol>
        </VRow>
        <VRow v-if="confirmAndShopBtn" class="v2-gutters pt-2">
          <VCol cols="12" md="8">
            <SBtn
              link-medium
              type="submit"
              block
              @click="shopWithDemo=true"
            >
              {{ $t('btn.confirmAndShop') }}
            </SBtn>
          </VCol>
        </VRow>
        <slot name="footer">
          <VRow v-if="!agent && !guest && !demo && !subscription" class="v2-gutters" data-testid="demo-website-link">
            <VCol
              class="text-center"
            >
              <p class="mb-2">
                {{ $t('account.demonstrator') }}
              </p>
              {{ $t('demo.p.signInAbove') }}
            </VCol>
          </VRow>
        </slot>
      </VContainer>
    </ObservedForm>
  </div>
</template>

<script lang="ts" setup>
import { SIGN_IN_PASSWORD_RULES } from '~/utils/validation/password'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import PasswordField from '~/components/base/fields/PasswordField.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import ErrorContainer from '~/components/base/ErrorContainer.vue'
import type { Credentials } from '~/types/auth'
import {checkRouteName} from "~/utils";

interface Props {
  demo?: boolean
  guest?: boolean
  agent?: boolean
  subscription?: boolean
  btnColor?: string
  usernameReadonly?: string
  confirmAndShopBtn?: boolean
  signInBtnText?: string
  options?: boolean
  allowPersist?: boolean
  alreadyExists?: string
  invalidCredentials?: boolean
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  demo: false,
  guest: false,
  agent: false,
  subscription: false,
  btnColor: 'primary',
  signInBtnText: undefined,
  usernameReadonly: undefined,
  confirmAndShopBtn: false,
  options: false,
  allowPersist: true,
  alreadyExists: '',
  invalidCredentials: false,
  error: undefined
})

const emits = defineEmits<{
  (e: 'changed' | 'reset-password'): void
  (e: 'update:alreadyExists', value: string): void
  (e: 'form-submit', credentials: Credentials): void
}>()

const $i18n = useI18n()
const $t = (key: string) => $i18n.ts(key)
const route = useRoute()
const router = useRouter()

const slots = useSlots()

const username = ref(props.usernameReadonly || '')
const password = ref('')
const persist = ref(false)
const shopWithDemo = ref(false)
const userName = ref<HTMLElement>()
const observer = useTemplateRef<InstanceType<typeof ObservedForm>>('observer')

watch(() => [username.value, password.value, persist.value], () => emits('changed'))
watch(() => props.alreadyExists, () => {
  username.value = props.alreadyExists
  if (username.value && userName.value) {
    userName.value.focus()
  }
})

const signInLabel = computed(() => {
  if (props.demo && props.allowPersist) {
    return $i18n.t('label.demonstrator')
  }
  if (checkRouteName(router.currentRoute.value.name, ['index', 'auth-sign-in', 'lang-index', 'lang-auth-sign-in']) && !router.currentRoute.value.query.invitation) {
    return $i18n.t('label.userID')
  }
  return $i18n.t('label.email')
})

const errorMessage = computed<string>(() => {
  if (props.error) {
    return props.error
  }
  return props.alreadyExists ? $t('error.p.emailExists') : props.invalidCredentials ? $t('error.p.invalidCredentials') : ''
})

const resetForm = () => {
  if (observer.value) {
    username.value = ''
    password.value = ''
    observer.value.resetForm()
  }
}

const resetAlreadyExists = () => {
  requestAnimationFrame(() => {
    resetForm()
    // The emit removes this button (v-if on alreadyExists), so a keyboard user's focus
    // would fall to the page body. Move it to the email field they now need to fill. AB#64780
    userName.value?.focus()
    emits('update:alreadyExists', '')
  })
}

const submit = () => {
  emits('form-submit', { username: username.value, password: password.value, persist: persist.value, shopWithDemo: shopWithDemo.value } as Credentials)
}

onMounted(() => {
  if (route.query?.username) {
    username.value = route.query.username as string
  }
})

defineExpose({
  resetForm
})
</script>

<style lang="scss" scoped>
  // The fields only render a details row when they carry a message
  // (hide-details-without-error), as in nuxt2. Both sit in one column, so nuxt2
  // left 20px under the message before the next field; the global error padding
  // in overrides.scss (4px) is tuned for one-field-per-column rows.
  :deep(.v-text-field.v-input--error) {
    padding-bottom: 20px;
  }

  .reset-link {
    color: rgb(var(--v-theme-header));
    text-transform: none;
    font-weight: 500;
  }
</style>
